import type { ComponentProps, ReactElement } from 'react'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import GeoContext from 'contexts/geoContext'
import { usePlaceByCoords } from 'hooks/usePlace'
import Places, { getPlaceByRegionCode } from 'constants/places'
import type OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import { getCanViewLuxPlusBenefits, getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import type { Region } from 'constants/geo'

interface Props {
  place?: App.Place
  title?: string | ReactElement
  subtitle?: string
  carouselId?: string
  campaigns?: Array<string>
}

function getPlace(countryCode: Region): App.Place
function getPlace(place: App.Place): App.Place
function getPlace(placeOrCountryCode: App.Place | string): App.Place {
  const isCountryCode = typeof placeOrCountryCode === 'string'
  const place: App.Place = isCountryCode ? getPlaceByRegionCode(placeOrCountryCode) : placeOrCountryCode
  const placeCanonicalNameOrName = place.canonicalName ?? place.name

  // There are some places that we don't have LuxPlus+ discounted offers yet, for those we fallback to Australia, which are:
  // - Tasmania
  // - New Zealand
  // - Northern Territory
  if (placeCanonicalNameOrName?.match(/([Tt]asmania)|([Nn]ew [Zz]ealand)|([Nn]orthern [Tt]erritory)/gm)) {
    return Places.Australia
  }

  return place
}

export function useExperienceLuxPlusCarouselProps(props: Props = {}) {
  const {
    place,
    title,
    subtitle,
    carouselId,
    campaigns,
  } = props

  const intl = useIntl()
  const isLuxPlusExperiencesEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedExperiences)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  const { currentRegionCode, latitude, longitude } = useContext(GeoContext)
  const [userPlace] = usePlaceByCoords(latitude, longitude)

  const luxPlusCarouselProps = useMemo<ComponentProps<typeof OfferCardCarouselSection> | undefined>(() => {
    if (!isLuxPlusExperiencesEnabled) return

    const currentPlace = getPlaceByRegionCode(currentRegionCode)
    const isMember = canViewLuxPlusBenefits
    const finalPlace = getPlace(place ?? userPlace ?? currentPlace)

    const discountedExperiencesFilter: App.OfferListFilters = {
      offerTypes: ['experience'],
      placeIds: [finalPlace.id],
      destinationId: finalPlace.id,
      destinationName: finalPlace.name,
      luxPlusFeatures: ['Member prices'],
      campaigns,
    }

    return {
      tileSize: 'medium',
      title: title ?? (isMember
        ? intl.formatMessage({
            id: 'eBsbQj-z8G-Llnw9c42C',
            defaultMessage: 'Local discounts exclusive to you',
            description: 'Carousel title for LuxPlus member showing exclusive local experience discounts',
          })
        : intl.formatMessage({
            id: 't38L600mSmQ3gymyHe6p',
            defaultMessage: 'Local discounts exclusive to LuxPlus+ members',
            description: 'Carousel title for non-member promoting LuxPlus exclusive experience discounts',
          })),
      subtitle: subtitle ?? intl.formatMessage({
        id: '9NYTGS3m0OP7olfNsZQy',
        defaultMessage: 'Enjoy discounted experiences without the need for travelling',
        description: 'Carousel subtitle for LuxPlus discounted experiences section',
      }),
      filters: {
        ...discountedExperiencesFilter,
      },
      allLink: `/search/experiences?${getSearchQueryFromFilter(discountedExperiencesFilter)}`,
      id: carouselId ?? 'discounted_experiences',
      hideWhenEmpty: true,
    }
  }, [intl, isLuxPlusExperiencesEnabled, currentRegionCode, canViewLuxPlusBenefits, place, userPlace, title, subtitle, carouselId, campaigns])

  return luxPlusCarouselProps
}
